import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'motion/react';
import { useQuery } from '@tanstack/react-query';
import { ReviewWriteForm } from './ReviewWriteForm';
import {
  getDefaultConfirmationData,
  manageBodyOverflow,
  cleanupBodyOverflow,
  handleModalClose,
} from '../utils/modalUtils';
import { ModalOverlay, ConfirmationOverlay } from './common/SharedStyles';
import { CompleteView } from './common/CompleteView';
import { COLORS, FONT_SIZES, FONT_WEIGHTS, BORDER_RADIUS, SPACING } from './common/constants';
import { ProductApi } from '../../../api/productApi';
import { isFormValid } from '../utils/validationUtils';
import { hasFormData, getInitialFormState } from '../utils/formUtils';
import { transformTaxonomyData } from '../utils/taxonomyUtils';
import type { ReviewModalProps, ReviewFormData } from './common/interfaces';

const Modal = styled(motion.div)`
  background: ${COLORS.background};
  border-radius: ${BORDER_RADIUS.lg};
  width: 686px;
  max-width: 90vw;
  max-height: 90vh;
  box-shadow: 0 8px 32px ${COLORS.shadow};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${SPACING.xl} ${SPACING.xl} 0px ${SPACING.xl};
`;

const Title = styled.div`
  font-size: ${FONT_SIZES.xl};
  font-weight: ${FONT_WEIGHTS.normal};
  color: ${COLORS.text};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: ${FONT_SIZES['2xl']} !important;
  font-weight: ${FONT_WEIGHTS.normal};
  color: #333;
  cursor: pointer;
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${SPACING.lg} ${SPACING.xl} ${SPACING.xl} ${SPACING.xl};
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xl};
`;

// Confirmation dialog styles
const ConfirmationModal = styled(motion.div)`
  background: ${COLORS.background};
  border-radius: ${BORDER_RADIUS.md};
  padding: ${SPACING.xl};
  max-width: 400px;
  width: 90%;
  box-shadow: 0 8px 32px ${COLORS.shadow};
`;

const ConfirmationTitle = styled.h3`
  font-size: ${FONT_SIZES.lg};
  font-weight: ${FONT_WEIGHTS.semibold};
  color: ${COLORS.text};
  margin: 0 0 ${SPACING.md} 0;
`;

const ConfirmationMessage = styled.p`
  font-size: ${FONT_SIZES.sm};
  color: ${COLORS.textSecondary};
  margin: 0 0 ${SPACING.xl} 0;
  line-height: 1.4;
`;

const ConfirmationButtons = styled.div`
  display: flex;
  gap: ${SPACING.md};
  justify-content: flex-end;
`;

const ConfirmationButton = styled.button<{ variant: 'primary' | 'secondary' }>`
  padding: ${SPACING.sm} ${SPACING.lg};
  border-radius: ${BORDER_RADIUS.sm};
  font-size: ${FONT_SIZES.sm};
  font-weight: ${FONT_WEIGHTS.medium};
  cursor: pointer;
  border: none;

  ${(props) =>
    props.variant === 'primary'
      ? `
    background: ${COLORS.primary};
    color: ${COLORS.white};
  `
      : `
    background: #f5f5f5;
    color: ${COLORS.text};
  `}

  &:hover {
    opacity: 0.9;
  }
`;

export const ReviewWritePcModal = ({ isOpen, productImage, onClose, onSubmit }: ReviewModalProps) => {
  // Form state (single object instead of multiple useState)
  const [formState, setFormState] = useState<ReviewFormData>(getInitialFormState());

  // Modal state
  const [isComplete, setIsComplete] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Taxonomy data query
  const { data: taxonomy } = useQuery({
    queryKey: ['taxonomy'],
    queryFn: ProductApi.getTaxonomy,
    enabled: isOpen,
  });

  const { skinTypeOptions, skinConcernOptions } = transformTaxonomyData(taxonomy);

  // Derived state
  const hasUnsavedChanges = hasFormData(formState);
  const isSubmitDisabled = !isFormValid(formState);

  // Form actions for cleaner prop passing
  const formActions = useMemo(
    () => ({
      setRating: (value: number) => setFormState((prev) => ({ ...prev, rating: value })),
      setTitle: (value: string) => setFormState((prev) => ({ ...prev, title: value })),
      setContent: (value: string) => setFormState((prev) => ({ ...prev, content: value })),
      setNickname: (value: string) => setFormState((prev) => ({ ...prev, nickname: value })),
      setSkinType: (value: string) => setFormState((prev) => ({ ...prev, skinType: value })),
      setSkinConcern: (value: string) => setFormState((prev) => ({ ...prev, skinConcern: value })),
      setImages: (value: File[]) => setFormState((prev) => ({ ...prev, images: value })),
      setRecommend: (value: string) => setFormState((prev) => ({ ...prev, recommend: value })),
    }),
    [],
  );

  const formOptions = {
    skinTypeOptions,
    skinConcernOptions,
  };

  // Reset form using shared utility
  const resetForm = useCallback(() => {
    setFormState(getInitialFormState());
  }, []);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    try {
      await onSubmit(formState);
      setIsComplete(true);
    } catch (error) {
      console.error('Form submission failed:', error);
      // Handle error appropriately
    }
  }, [onSubmit, formState]);

  // Handle modal close with confirmation
  const handleClose = useCallback(() => {
    handleModalClose({
      hasUnsavedChanges,
      isComplete,
      onShowConfirmation: () => setShowConfirmation(true),
      onClose,
    });
  }, [hasUnsavedChanges, isComplete, onClose]);

  // Confirm close and clear data
  const handleConfirmClose = useCallback(() => {
    setShowConfirmation(false);
    resetForm();
    setIsComplete(false);
    onClose();
  }, [resetForm, onClose]);

  // Cancel close confirmation
  const handleCancelClose = useCallback(() => {
    setShowConfirmation(false);
  }, []);

  // Manage body overflow
  useEffect(() => {
    manageBodyOverflow(isOpen);
    return () => {
      cleanupBodyOverflow();
    };
  }, [isOpen]);

  // Clear form data when modal closes
  useEffect(() => {
    if (!isOpen && !showConfirmation) {
      resetForm();
      setIsComplete(false);
    }
  }, [isOpen, showConfirmation, resetForm]);

  const confirmationData = getDefaultConfirmationData();

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <ModalOverlay initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={handleClose}>
            <Modal
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ type: 'spring', damping: 25, stiffness: 500 }}
              onClick={(e) => e.stopPropagation()}
            >
              {!isComplete && (
                <Header>
                  <Title>Review this product</Title>
                  <CloseButton onClick={handleClose}>×</CloseButton>
                </Header>
              )}
              <Content>
                {!isComplete ? (
                  <ReviewWriteForm
                    formData={formState}
                    formActions={formActions}
                    options={formOptions}
                    productImage={productImage}
                    isSubmitDisabled={isSubmitDisabled}
                    onSubmit={handleSubmit}
                    variant="pc"
                  />
                ) : (
                  <CompleteView onContinue={onClose} variant="pc" />
                )}
              </Content>
            </Modal>
          </ModalOverlay>
        )}
      </AnimatePresence>

      {/* Confirmation Dialog */}
      <AnimatePresence>
        {showConfirmation && (
          <ConfirmationOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCancelClose}
          >
            <ConfirmationModal
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ConfirmationTitle>{confirmationData.title}</ConfirmationTitle>
              <ConfirmationMessage>{confirmationData.message}</ConfirmationMessage>
              <ConfirmationButtons>
                <ConfirmationButton variant="secondary" onClick={handleCancelClose}>
                  {confirmationData.cancelText}
                </ConfirmationButton>
                <ConfirmationButton variant="primary" onClick={handleConfirmClose}>
                  {confirmationData.confirmText}
                </ConfirmationButton>
              </ConfirmationButtons>
            </ConfirmationModal>
          </ConfirmationOverlay>
        )}
      </AnimatePresence>
    </>
  );
};
